import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usersApi } from '@/lib/api';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export default function UserCard({ user: profileUser, onFollowChange }) {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [isFollowing, setIsFollowing] = useState(profileUser.is_following || false);
  const [loading, setLoading] = useState(false);

  const isOwnProfile = currentUser?.username === profileUser.username;

  const handleFollow = async (e) => {
    e.stopPropagation();
    if (!currentUser) {
      navigate('/login');
      return;
    }
    setLoading(true);
    try {
      if (isFollowing) {
        await usersApi.unfollow(profileUser.username);
        setIsFollowing(false);
        toast.success(`Unfollowed @${profileUser.username}`);
      } else {
        await usersApi.follow(profileUser.username);
        setIsFollowing(true);
        toast.success(`Following @${profileUser.username}`);
      }
      onFollowChange?.();
    } catch (err) {
      toast.error(err.message || 'Failed to update follow');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      onClick={() => navigate(`/profile/${profileUser.username}`)}
      className="flex items-center gap-3 p-3 rounded-xl hover:bg-cream-dark cursor-pointer transition-colors group"
    >
      <Avatar className="w-10 h-10 flex-shrink-0">
        <AvatarImage src={profileUser.avatar_url} alt={profileUser.display_name} />
        <AvatarFallback className="bg-terra text-cream text-sm font-semibold">
          {getInitials(profileUser.display_name || profileUser.username)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-ink group-hover:text-terra transition-colors truncate">
          {profileUser.display_name || profileUser.username}
        </p>
        <p className="text-xs text-ink-muted truncate">@{profileUser.username}</p>
        {profileUser.followers_count !== undefined && (
          <p className="text-xs text-ink-muted">
            {profileUser.followers_count} follower{profileUser.followers_count !== 1 ? 's' : ''}
          </p>
        )}
      </div>
      {!isOwnProfile && currentUser && (
        <Button
          variant={isFollowing ? 'outline' : 'default'}
          size="sm"
          onClick={handleFollow}
          disabled={loading}
          className={
            isFollowing
              ? 'border-terra text-terra hover:bg-terra/10 text-xs px-3'
              : 'bg-terra hover:bg-terra/90 text-cream text-xs px-3'
          }
        >
          {isFollowing ? 'Following' : 'Follow'}
        </Button>
      )}
    </div>
  );
}
